import restore from 'api/restorePassword'
import { Formik } from 'formik'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as yup from 'yup'
import ReCAPTCHA from 'react-google-recaptcha'

import { useTranslation } from 'next-i18next'

import ErrorMessage from 'components/ErrorMessage/ErrorMessage'
import Field from 'components/Form/Field/Field'
import Form from 'components/Form/Form'
import { RecoveryIcon } from 'components/Images/Icons'
import usePlausible from 'utils/hooks/usePlausible'
import { PlausibleEvent } from 'enums/plausible'
import validateMessage from './validateMessage'
import handleResponseError from './handleResponseError'
import ErrorCode from './ErrorCodes'

import styles from './RestoreForm.module.scss'
import SubmitButton from 'components/SubmitButton'

const RestoreForm = ({ referrer }) => {
  const setEvent = usePlausible()
  const [t] = useTranslation(['main', 'error'])

  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const captchaRef = useRef(null)

  const onSubmit = useCallback(
    async (values) => {
      setEvent(PlausibleEvent.ClickedSignInSendRecovery, {
        step: 3,
        referrer,
        email: values.email,
      })

      try {
        const captcha = await captchaRef.current.executeAsync()

        const res = await restore({ ...values, captcha })
        if (res.status !== 204) {
          const { payload, code } = res.data
          switch (code) {
            case ErrorCode.VALIDATION_ERRORS:
              if (payload?.errors) {
                const wrappedErrors = handleResponseError(res)
                setError(wrappedErrors)
              }
              break
            case ErrorCode.RECOVERY_USER_WITH_SPECIFIED_EMAIL_NOT_FOUND:
              setError(validateMessage.emailNotFound)
              break
            default:
              setError(validateMessage.serverError)
          }
        } else {
          setError('')
          setSuccess(true)
        }
      } catch (e) {
        console.error(e)

        setError(validateMessage.serverError)
      }
    },
    [setSuccess],
  )

  const [email, setEmail] = useState('')
  useEffect(() => {
    setEmail(localStorage.getItem('saved_email') || '')
  }, [setEmail])

  const initialValues = useMemo(() => {
    return { email }
  }, [email])

  return (
    <>
      {success ? (
        <div className={styles.success}>
          <img src="/images/util/done.svg" alt="success" />
          <p>{t('main:recoveryConfirmLinkBeenSent')}</p>
        </div>
      ) : (
        <>
          <div className={styles.recovery_icon}>
            <RecoveryIcon />
          </div>
          <div className={styles.tip}>{t('main:recoveryToResetPass')}</div>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={yup.object({
              email: yup.string().email(t('error:InvalidEmail')).required(t('error:requiredField')),
            })}
            onSubmit={onSubmit}
          >
            <Form>
              <Field
                placeholder={t('main:recoveryEmail')}
                name="email"
                type="email"
                id="recovery-form-field-input"
              />
              {error && <ErrorMessage message={error} />}

              <SubmitButton className={styles.submitButton} id="recovery-form-submit-button">
                {t('main:recoverySend').toString()}
              </SubmitButton>
            </Form>
          </Formik>

          <ReCAPTCHA
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA}
            ref={captchaRef}
            size="invisible"
          />
        </>
      )}
    </>
  )
}

export default RestoreForm
